import React from 'react';
import { z } from 'zod';
import { ChevronDownIcon, ChevronUpIcon, ChevronsUpDownIcon } from 'lucide-react';
import { DateTime } from 'src_common/utils/time/time';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { CustomerButton } from 'src/components/FraudAndCompliance/CustomerButton/CustomerButton';
import {
    ChevronDownIconWrapper,
    Select,
    SelectWrapper,
} from 'src/components/FraudAndCompliance/FraudAndCompliancesTab/FraudAndCompliances/components/CommonTable.styles';
import { MohPopupTriggeredNotificationType } from 'src_server/types/notificationData';
import {
    ColumnHeader,
    FilterLabel,
    FilterSelect,
    FilterWrapper,
    SectionHeader,
    SectionHeaderRow,
    SectionMessage,
    SectionWrapper,
    SortableColumnHeader,
    SortableColumnHeaderContent,
    SortIcon,
    TableCell,
    TableHeaderRow,
    TableRow,
    TableWrapper,
} from './MohPopupTriggersTab.style';
import {
    MOH_POPUP_TRIGGERS_RISK_LEVEL_FILTER_OPTIONS,
    MohPopupTriggersRiskLevelFilterType,
    MohPopupTriggersSectionType,
    MohPopupTriggersSortDirectionType,
    MohPopupTriggersSortFieldType,
    MohPopupTriggersTabState,
} from './MohPopupTriggersTab.state';

const ACTIONS = {
    your: [
        { action: 'cancel', name: 'Mark as reviewed' },
        { action: 'unassign', name: 'Unassign' },
    ],
    none: [{ action: 'assign', name: 'Assign to me' }],
    other: [{ action: 'unassign', name: 'Unassign' }],
} as const;

type ActionsType = MohPopupTriggersSectionType;

interface OptionType {
    action: string;
    name: string;
}

const getOption = (option: OptionType, id: string): React.ReactElement => {
    return (
        <option
            key={`${id}-${option.action}`}
            value={option.action}
        >
            {option.name}
        </option>
    );
};

interface MohPopupTriggersSectionPropsType {
    type: ActionsType;
    header: string;
    dataTest: string;
    notifications: Array<MohPopupTriggeredNotificationType>;
    state: MohPopupTriggersTabState;
    isFinished: boolean;
}

const formatDateTime = (value: string | undefined): string =>
    value === undefined ? '-' : (DateTime.from(value)?.format('DD-MM-YYYY HH:mm') ?? '-');

const TriggerIdZod = z.coerce.number().int();

const getTriggerId = (notification: MohPopupTriggeredNotificationType): string => {
    const triggerId = notification.details.triggerId;
    return triggerId === undefined ? notification.id : String(triggerId);
};

const getTriggerIdAsNumber = (notification: MohPopupTriggeredNotificationType): number | undefined => {
    const triggerId = notification.details.triggerId;
    const parseResult = TriggerIdZod.safeParse(triggerId);
    return parseResult.success ? parseResult.data : undefined;
};

const getTriggerName = (notification: MohPopupTriggeredNotificationType): string => {
    const triggerName = notification.details.triggerName?.trim();
    if (triggerName !== undefined && triggerName.length > 0) {
        return triggerName;
    }

    const automationName = notification.details.automationName?.trim();
    if (automationName !== undefined && automationName.length > 0) {
        return automationName;
    }

    return '-';
};

const getPopupWithId = (state: MohPopupTriggersTabState, notification: MohPopupTriggeredNotificationType): string => {
    const triggerIdAsNumber = getTriggerIdAsNumber(notification);
    const popupTitleFromCms = state.getPopupTitleForTriggerId(triggerIdAsNumber);
    const name = popupTitleFromCms ?? getTriggerName(notification);
    const id = getTriggerId(notification);
    return `${name} (${id})`;
};

const getTriggeredAt = (notification: MohPopupTriggeredNotificationType): string => {
    return notification.details.triggeredAt ?? notification.createdAt;
};

const getRiskLevel = (notification: MohPopupTriggeredNotificationType): string => {
    const riskLevel = notification.details.riskLevel?.trim();
    return riskLevel !== undefined && riskLevel.length > 0 ? riskLevel.toUpperCase() : '-';
};

const isRiskLevelFilter = (value: string): value is MohPopupTriggersRiskLevelFilterType => {
    return MOH_POPUP_TRIGGERS_RISK_LEVEL_FILTER_OPTIONS.some((option) => option.value === value);
};

const getSortIcon = (
    field: MohPopupTriggersSortFieldType,
    sortField: MohPopupTriggersSortFieldType,
    sortDirection: MohPopupTriggersSortDirectionType
): React.ReactElement => {
    if (field !== sortField) {
        return <ChevronsUpDownIcon />;
    }

    if (sortDirection === 'desc') {
        return <ChevronDownIcon />;
    }

    return <ChevronUpIcon />;
};

const getSortIndicator = (
    field: MohPopupTriggersSortFieldType,
    sortField: MohPopupTriggersSortFieldType,
    sortDirection: MohPopupTriggersSortDirectionType
): React.ReactElement => {
    return (
        <SortIcon
            isActive={field === sortField}
            aria-hidden={true}
        >
            {getSortIcon(field, sortField, sortDirection)}
        </SortIcon>
    );
};

const getAriaSort = (
    field: MohPopupTriggersSortFieldType,
    sortField: MohPopupTriggersSortFieldType,
    sortDirection: MohPopupTriggersSortDirectionType
): 'ascending' | 'descending' | 'none' => {
    if (field !== sortField) {
        return 'none';
    }

    return sortDirection === 'asc' ? 'ascending' : 'descending';
};

interface MohPopupTriggersRiskLevelFilterPropsType {
    section: MohPopupTriggersSectionType;
    state: MohPopupTriggersTabState;
}

export const MohPopupTriggersRiskLevelFilter = observer(
    'MohPopupTriggersRiskLevelFilter',
    ({ section, state }: MohPopupTriggersRiskLevelFilterPropsType): React.ReactElement => {
        const filterId = `moh-popup-triggers-risk-level-filter-${section}`;

        return (
            <FilterWrapper data-test={filterId}>
                <FilterLabel htmlFor={filterId}>Risk Level</FilterLabel>
                <FilterSelect
                    id={filterId}
                    value={state.getRiskLevelFilter(section)}
                    onChange={(event): void => {
                        const nextFilter = event.target.value;
                        if (isRiskLevelFilter(nextFilter)) {
                            state.setRiskLevelFilter(section, nextFilter);
                        }
                    }}
                    data-test={`${filterId}-select`}
                >
                    {MOH_POPUP_TRIGGERS_RISK_LEVEL_FILTER_OPTIONS.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                        >
                            {option.label}
                        </option>
                    ))}
                </FilterSelect>
            </FilterWrapper>
        );
    }
);

const getAppliedAction = (state: MohPopupTriggersTabState, notification: MohPopupTriggeredNotificationType): string => {
    const automationName = notification.details.automationName?.trim();
    if (automationName !== undefined && automationName.length > 0) {
        return state.getAppliedActionForPopupName(automationName);
    }

    return '-';
};

export const MohPopupTriggersSection = observer(
    'MohPopupTriggersSection',
    ({
        type,
        header,
        dataTest,
        notifications,
        state,
        isFinished,
    }: MohPopupTriggersSectionPropsType): React.ReactElement => {
        const { models } = useAppStateContext();

        const doAction = async (
            event: React.ChangeEvent<HTMLSelectElement>,
            notification: MohPopupTriggeredNotificationType
        ): Promise<void> => {
            const action = event.target.value;

            const mutationAction =
                action === 'unassign'
                    ? 'unassign'
                    : action === 'assign'
                      ? 'assign'
                      : action === 'cancel'
                        ? 'close'
                        : null;

            if (mutationAction === null) {
                return;
            }

            try {
                await state.updateNotification(notification, mutationAction);
            } catch (error) {
                console.error(`MohPopupTriggers ${action} error`, error);
            }
        };

        const areNoItems = isFinished && notifications.length === 0;
        const emptyMessage =
            state.getRiskLevelFilter(type) === 'all' ? 'There are no items' : 'No items match the selected risk level';

        return (
            <SectionWrapper data-test={dataTest}>
                <SectionHeaderRow>
                    <SectionHeader data-test='section-header'>{header}</SectionHeader>
                    <MohPopupTriggersRiskLevelFilter
                        section={type}
                        state={state}
                    />
                </SectionHeaderRow>
                {areNoItems ? (
                    <SectionMessage>{emptyMessage}</SectionMessage>
                ) : (
                    <TableWrapper>
                        <thead>
                            <TableHeaderRow>
                                <ColumnHeader>Pop-up and ID</ColumnHeader>
                                <SortableColumnHeader
                                    onClick={(): void => state.setSortField('date-and-time')}
                                    aria-sort={getAriaSort('date-and-time', state.sortField, state.sortDirection)}
                                    data-test='moh-popup-triggers-sort-date-and-time'
                                >
                                    <SortableColumnHeaderContent>
                                        Date and Time
                                        {getSortIndicator('date-and-time', state.sortField, state.sortDirection)}
                                    </SortableColumnHeaderContent>
                                </SortableColumnHeader>
                                <ColumnHeader>Account</ColumnHeader>
                                <SortableColumnHeader
                                    onClick={(): void => state.setSortField('risk-level')}
                                    aria-sort={getAriaSort('risk-level', state.sortField, state.sortDirection)}
                                    data-test='moh-popup-triggers-sort-risk-level'
                                >
                                    <SortableColumnHeaderContent>
                                        Risk Level
                                        {getSortIndicator('risk-level', state.sortField, state.sortDirection)}
                                    </SortableColumnHeaderContent>
                                </SortableColumnHeader>
                                <ColumnHeader>Applied Action</ColumnHeader>
                                <ColumnHeader>Action</ColumnHeader>
                            </TableHeaderRow>
                        </thead>
                        <tbody>
                            {notifications.map((notification) => (
                                <TableRow
                                    key={notification.id}
                                    section={type}
                                    data-test={`moh-popup-triggered-notification-row-${notification.accountId}`}
                                >
                                    <TableCell data-test='moh-popup-trigger-name-id'>
                                        {getPopupWithId(state, notification)}
                                    </TableCell>
                                    <TableCell data-test='moh-popup-triggered-at'>
                                        {formatDateTime(getTriggeredAt(notification))}
                                    </TableCell>
                                    <TableCell data-test='moh-popup-customer'>
                                        <CustomerButton
                                            customerId={notification.accountId}
                                            models={models}
                                        />
                                    </TableCell>
                                    <TableCell data-test='moh-popup-risk-level'>{getRiskLevel(notification)}</TableCell>
                                    <TableCell data-test='moh-popup-applied-action'>
                                        {getAppliedAction(state, notification)}
                                    </TableCell>
                                    <TableCell disableColor={true}>
                                        <SelectWrapper>
                                            <Select
                                                name='action'
                                                defaultValue=''
                                                onChange={(event): void => {
                                                    void doAction(event, notification);
                                                }}
                                                data-test='moh-popup-action-form'
                                            >
                                                <option value=''>Choose an action</option>
                                                {ACTIONS[type].map((option) => getOption(option, notification.id))}
                                            </Select>
                                            <ChevronDownIconWrapper />
                                        </SelectWrapper>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </tbody>
                    </TableWrapper>
                )}
            </SectionWrapper>
        );
    }
);
